class EncryptDecrypt {
    private crypto;
    private readonly NONCE_LENGTH = 12;
    private readonly AUTH_TAG_LENGTH = 16;

    constructor(crypto: any) {
        this.crypto = crypto;
    }

    encrypt(dataToEncrypt: string, keyPart: string, salt: string, source = 'browser') {
        if (!dataToEncrypt) {
            throw new Error(`Data provided by ${source} to encrypt is invalid`);
        }
        if (!keyPart) {
            throw new Error(`Cannot generate Key for encryption without KeyPart provided by ${source}`);
        }
        if (!salt) {
            throw new Error(`Cannot generate Key for encryption without Salt provided by ${source}`);
        }

        const keyString = keyPart + salt;

        // set the key - NOTE: iv will be the same as the key
        const iv = keyString;

        // generate the cipher and perform encryption
        const cipher = this.crypto.createCipheriv('aes-128-cbc', keyString, Uint8Array.from(iv as any));
        let dataEncrypted = cipher.update(dataToEncrypt, 'utf8', 'base64');
        dataEncrypted += cipher.final('base64');

        return dataEncrypted;
    }

    decrypt(dataToDecrypt: string, keyPart: string, salt: string, source = 'browser') {
        if (!dataToDecrypt) {
            throw new Error(`Data provided by ${source} to decrypt is invalid`);
        }
        if (!keyPart) {
            throw new Error(`Cannot generate Key for decryption without KeyPart provided by ${source}`);
        }
        if (!salt) {
            throw new Error(`Cannot generate Key for decryption without Salt provided by ${source}`);
        }

        const keyString = keyPart + salt;

        // set the key - NOTE: iv will be the same as the key
        const iv = keyString;

        // generate the decipher and perform decryption
        const decipher = this.crypto.createDecipheriv('aes-128-cbc', keyString, Uint8Array.from(iv as any));
        let dataDecrypted = decipher.update(dataToDecrypt, 'base64', 'utf8');
        dataDecrypted += decipher.final('utf8');

        return dataDecrypted;
    }

    encryptGcm(dataToEncrypt: string, key: string, source = 'browser'): string {
        if (!dataToEncrypt) {
            throw new Error(`Data provided by ${source} to encrypt is invalid`);
        }
        if (!key) {
            throw new Error(`No key provided by ${source}, cannot encrypt data`);
        }

        const nonce = this.crypto.randomBytes(this.NONCE_LENGTH);
        const cipher = this.crypto.createCipheriv('aes-128-gcm', key, Uint8Array.from(nonce));
        const encryptedValue = Buffer.concat([
            cipher.update(dataToEncrypt),
            cipher.final()
        ]);

        const result = Buffer.concat([
            nonce,
            cipher.getAuthTag(),
            encryptedValue
        ]);

        // Output needs to be base64 encoded so no non-printable characters are included for DB
        return result.toString('base64');
    }

    decryptGcm(dataToDecrypt: string, key: string, source = 'browser'): string {
        if (!dataToDecrypt) {
            throw new Error(`Data provided by ${source} to decrypt is invalid`);
        }
        if (!key) {
            throw new Error(`No key provided by ${source}, cannot decrypt data`);
        }

        const dataBuffer = Buffer.from(dataToDecrypt, 'base64');
        const nonce = dataBuffer.subarray(0, this.NONCE_LENGTH);
        const authTag = dataBuffer.subarray(this.NONCE_LENGTH, (this.NONCE_LENGTH + this.AUTH_TAG_LENGTH));
        const encryptedData = dataBuffer.subarray(this.NONCE_LENGTH + this.AUTH_TAG_LENGTH);

        const decipher = this.crypto.createDecipheriv('aes-128-gcm', key, Uint8Array.from(nonce));
        decipher.setAuthTag(authTag);

        return decipher.update(encryptedData) + decipher.final('utf-8');
    }
}

export default EncryptDecrypt;